import React, { useMemo, useState } from "react";
import { Calendar, Clock, CheckCircle, Star, ChevronRight } from "lucide-react";

// Design tokens optimized for mobile
const DESIGN = {
  colors: {
    primary: {
      50: "#f0f9ff",
      500: "#0ea5e9",
      600: "#0284c7",
    },
    neutral: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
    },
    success: {
      50: "#f0fdf4",
      500: "#22c55e",
      600: "#16a34a",
    },
    warning: {
      50: "#fffbeb",
      500: "#f59e0b",
      600: "#d97706",
    },
    error: {
      50: "#fef2f2",
      500: "#ef4444",
      600: "#dc2626",
    }
  },
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
  },
  borderRadius: {
    sm: "6px",
    md: "8px",
    lg: "12px",
    xl: "16px",
  },
  shadow: {
    sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: "0 2px 4px -1px rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    lg: "0 4px 6px -2px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  }
};

const StatCard = ({ 
  icon, 
  gradient, 
  value, 
  label, 
  active = false, 
  onClick,
  showArrow = false,
  compact = false
}) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    style={{
      all: "unset",
      cursor: onClick ? "pointer" : "default",
      backgroundColor: "#ffffff",
      borderRadius: DESIGN.borderRadius.lg,
      boxShadow: active ? DESIGN.shadow.lg : DESIGN.shadow.sm,
      padding: compact ? DESIGN.spacing.md : DESIGN.spacing.lg,
      border: active 
        ? `2px solid ${DESIGN.colors.primary[500]}` 
        : `1px solid ${DESIGN.colors.neutral[200]}`,
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "center",
      width: "100%",
      minHeight: compact ? "70px" : "80px",
      position: "relative",
      overflow: "hidden",
      flexShrink: 0,
    }}
    className="stat-card"
    onMouseEnter={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow = DESIGN.shadow.lg;
      }
    }}
    onMouseLeave={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = active ? DESIGN.shadow.lg : DESIGN.shadow.sm;
      }
    }}
    onTouchStart={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "scale(0.98)";
      }
    }}
    onTouchEnd={(e) => {
      if (onClick) {
        e.currentTarget.style.transform = "scale(1)";
      }
    }}
  >
    {/* Background accent */}
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "2px",
        background: gradient,
        opacity: active ? 1 : 0.7,
      }}
    />
    
    <div
      style={{
        padding: compact ? DESIGN.spacing.sm : DESIGN.spacing.md,
        borderRadius: DESIGN.borderRadius.md,
        marginRight: compact ? DESIGN.spacing.sm : DESIGN.spacing.md,
        background: gradient,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        width: compact ? "36px" : "40px",
        height: compact ? "36px" : "40px",
      }}
    >
      {React.cloneElement(icon, { size: compact ? 18 : 20 })}
    </div>
    
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      alignItems: "flex-start",
      flex: 1,
      minWidth: 0,
      gap: "2px",
    }}>
      <span
        style={{
          fontSize: compact ? "20px" : "22px",
          lineHeight: 1.1,
          fontWeight: 700,
          color: DESIGN.colors.neutral[800],
          letterSpacing: "-0.01em",
        }}
      >
        {value}
      </span>
      <span style={{ 
        fontSize: compact ? "12px" : "13px", 
        fontWeight: 500, 
        color: DESIGN.colors.neutral[600],
        textAlign: "left",
        lineHeight: 1.2,
      }}>
        {label}
      </span>
    </div>

    {showArrow && onClick && (
      <ChevronRight 
        size={14} 
        color={DESIGN.colors.neutral[400]}
        style={{ 
          marginLeft: DESIGN.spacing.sm,
          opacity: active ? 1 : 0.5,
          transition: "opacity 0.2s ease",
          flexShrink: 0,
        }}
      />
    )}
  </button>
);

const StatsGrid = ({ 
  appointments = [], 
  reviews = [], 
  onSelect,
  activeFilter = "all",
  showArrows = true,
  variant = "default" // "default" | "compact" | "scrollable"
}) => {
  const stats = useMemo(() => {
    const total = appointments.length;
    const pending = appointments.filter((a) => 
      (a.status || "").toLowerCase() === "pending"
    ).length;
    const confirmed = appointments.filter((a) => 
      (a.status || "").toLowerCase() === "confirmed"
    ).length;
    const completed = appointments.filter((a) => 
      (a.status || "").toLowerCase() === "completed"
    ).length;
    
    return { total, pending, confirmed, completed };
  }, [appointments]);

  const handleStatClick = (key) => {
    if (onSelect) {
      onSelect(key);
    }
  };

  const statConfigs = [
    {
      key: "all",
      icon: <Calendar />,
      gradient: `linear-gradient(135deg, ${DESIGN.colors.primary[500]}, ${DESIGN.colors.primary[600]})`,
      value: stats.total,
      label: "Total Appointments",
    },
    {
      key: "pending",
      icon: <Clock />,
      gradient: `linear-gradient(135deg, ${DESIGN.colors.warning[500]}, ${DESIGN.colors.warning[600]})`,
      value: stats.pending,
      label: "Pending",
    },
    {
      key: "confirmed",
      icon: <CheckCircle />,
      gradient: `linear-gradient(135deg, ${DESIGN.colors.success[500]}, ${DESIGN.colors.success[600]})`,
      value: stats.confirmed,
      label: "Confirmed",
    },
    {
      key: "reviews",
      icon: <Star />,
      gradient: `linear-gradient(135deg, #8b5cf6, #7c3aed)`,
      value: reviews.length,
      label: "Reviews",
    },
  ];

  const isCompact = variant === "compact";
  const isScrollable = variant === "scrollable";

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        .stats-grid {
          display: grid;
          grid-template-columns: ${isCompact ? "repeat(2, 1fr)" : "repeat(2, 1fr)"};
          gap: ${isCompact ? DESIGN.spacing.sm : DESIGN.spacing.md};
          width: 100%;
        }
        
        .stat-card {
          user-select: none;
          -webkit-user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        
        .stat-card:active {
          transform: scale(0.98) !important;
        }
        
        /* Mobile-first: Default to scrollable on small screens */
        @media (max-width: 767px) {
          .stats-grid.scrollable {
            display: flex;
            overflow-x: auto;
            scroll-snap-type: x mandatory;
            scroll-padding: ${DESIGN.spacing.md};
            padding-bottom: ${DESIGN.spacing.sm};
            -webkit-overflow-scrolling: touch;
            gap: ${DESIGN.spacing.md};
          }
          
          .stats-grid.scrollable::-webkit-scrollbar {
            display: none;
          }
          
          .stats-grid.scrollable .stat-card {
            min-width: calc(75vw - ${DESIGN.spacing.md});
            scroll-snap-align: start;
            flex-shrink: 0;
          }
        }
        
        /* Tablet */
        @media (min-width: 768px) and (max-width: 1023px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: ${DESIGN.spacing.lg};
          }
          
          .stats-grid.scrollable {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        
        /* Desktop */
        @media (min-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: ${DESIGN.spacing.lg};
          }
        }
        
        /* Large desktop */
        @media (min-width: 1200px) {
          .stats-grid {
            gap: ${DESIGN.spacing.xl};
          }
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .stat-card {
            transition: none !important;
          }
        }
        
        /* Mobile scroll indicator */
        .mobile-scroll-hint {
          display: none;
          text-align: center;
          padding: ${DESIGN.spacing.sm};
          color: ${DESIGN.colors.neutral[500]};
          font-size: 11px;
          font-weight: 500;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        
        @media (max-width: 767px) {
          .mobile-scroll-hint {
            display: ${isScrollable ? 'block' : 'none'};
          }
        }
        
        /* Very small mobile devices */
        @media (max-width: 360px) {
          .stats-grid.scrollable .stat-card {
            min-width: calc(80vw - ${DESIGN.spacing.md});
          }
          
          .stat-card {
            min-height: ${isCompact ? '60px' : '70px'} !important;
          }
        }
        
        /* Safe area support for notches */
        @supports(padding: max(0px)) {
          .stats-grid.scrollable {
            padding-left: max(${DESIGN.spacing.md}, env(safe-area-inset-left));
            padding-right: max(${DESIGN.spacing.md}, env(safe-area-inset-right));
          }
        }
      `}</style>

      <div className={`stats-grid ${isScrollable ? 'scrollable' : ''}`}>
        {statConfigs.map((stat) => (
          <StatCard
            key={stat.key}
            icon={stat.icon}
            gradient={stat.gradient}
            value={stat.value}
            label={stat.label}
            active={activeFilter === stat.key}
            onClick={() => handleStatClick(stat.key)}
            showArrow={showArrows}
            compact={isCompact}
          />
        ))}
      </div>
      
      <div className="mobile-scroll-hint">
        Swipe to see more ›
      </div>
    </div>
  );
};

// Add default props
StatsGrid.defaultProps = {
  appointments: [],
  reviews: [],
  showArrows: true,
  variant: "default",
};

export default StatsGrid;